import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { TableProps } from 'components/Table';
import utils from 'utils';
import selectors from 'state/selectors';

export const useTableColumns = () => {
  const t = utils.intl.useTranslator();
  const userPreference = useSelector(selectors.user.getUserPreference);
  const { dateFormat } = useSelector(selectors.user.getUserPreference);

  const tableColumns: TableProps['columns'] = useMemo(() => [
    {
      accessor: 'dateAndTime',
      Header: t('Date and Time'),
      filterType: 'date',
      Cell: ({ value }: { value: number }) => utils.date.convertDate(value, userPreference.timeZone),
      filter: utils.date.epochFilterFunction(userPreference.timeZone),
      dateFormat: userPreference.dateFormat,
    },
    {
      accessor: 'sender',
      Header: t('Sender'),
    },
    {
      accessor: 'typeDescription',
      Header: t('Type description'),
    },
    {
      accessor: 'rejectionReason',
      Header: t('Reject Reason'),
      Cell: ({ value }: { value: null | string }) => value ?? '',
    },
    {
      accessor: 'logNotes',
      Header: t('Log notes'),
    },
    {
      accessor: 'causal',
      Header: t('Causal'),
      filterType: 'select',
    },
    {
      accessor: 'wfDefinition.wfName',
      Header: t('WfName'),
    },
  ], [t, userPreference]);

  const historyColumns: TableProps['columns'] = useMemo(() => [
    { accessor: 'company', Header: t('company') },
    { accessor: 'documentName', Header: t('documentName') },
    { accessor: 'documentType', Header: t('documentType') },
    { accessor: 'currentUser', Header: t('currentUser') },
    { accessor: 'currentUsername', Header: t('currentUsername') },
    { accessor: 'senderUser', Header: t('senderUser') },
    { accessor: 'senderUsername', Header: t('senderUsername') },
    {
      accessor: 'insDate',
      Header: t('insDate'),
      filterType: 'date',
      filter: utils.date.epochFilterFunction(userPreference.timeZone),
      Cell: ({ value }: { value: number }) => utils.date.convertDate(value, userPreference.timeZone ?? 'UTC'),
      dateFormat: dateFormat,
    },
    { accessor: 'protocolIn', Header: t('protocolIn') },
    { accessor: 'bucketPath', Header: t('bucketPath') },
    { accessor: 'position', Header: t('position') },
    { accessor: 'memo', Header: t('memo') },
    {
      accessor: 'priority',
      Header: t('priority'),
      hasLocalKeys: ['LocalizableStringFe', 'documentHistory'],
    },
    {
      accessor: 'status',
      Header: t('status'),
      hasLocalKeys: ['LocalizableStringFe', 'documentHistory'],
    },
    { accessor: 'groupName', Header: t('groupName') },
    { accessor: 'fromAdr', Header: t('fromAdr') },
    { accessor: 'incChannel', Header: t('incChannel') },
  ], [t, userPreference, dateFormat]);

  const tableColumnsGroup: TableProps['columns'] = useMemo(() => [
    {
      accessor: 'idGroup',
      Header: t('idGroup'),
    },
    {
      accessor: 'groupName',
      Header: t('groupName'),
    },
    {
      accessor: 'amountLimit',
      Header: t('amountLimit'),
      Cell: ({ value, cell }: { value?: number | null; cell: { row: { original: { currency?: string } } } }) => {
        if (value == null) return '';
        return value + ' ' + (cell.row.original?.currency ?? '');
      },
    },
  ], [t]);

  const tableColumnsUsers: TableProps['columns'] = useMemo(() => [
    {
      accessor: 'username',
      Header: t('username'),
    },
    {
      accessor: 'name',
      Header: t('name'),
    },
  ], [t]);

  const tableColumnsTasks: TableProps['columns'] = useMemo(() => [
    {
      accessor: 'idTask',
      Header: t('ID Task'),
    },
    {
      accessor: 'taskName',
      Header: t('Task Name'),
    },
  ], [t]);

  return {
    tableColumns,
    historyColumns,
    tableColumnsGroup,
    tableColumnsUsers,
    tableColumnsTasks,
  };
};